import { useDeleteUser } from "api/tenant/service";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody } from "reactstrap";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useGetTenants } from "api/tenant/service";
import ReactTable from "components/ReactTable/ReactTable";
import ReactTable2 from "components/ReactTable/ReactTable2";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import TenantListFilters from "./components/TenantListFilters";
import TenantListCards from "./components/TenantListCards";

const TenantList = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const deleteUserMutation = useDeleteUser();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const { data: tenantsData, isLoading } = useGetTenants(page, pageSize, searchTerm, filterStatus);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus]);

  const serverPageCount = tenantsData?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const isTheme2 = shellThemeVariant === "2";
  const TenantsTable = isTheme2 ? ReactTable2 : ReactTable;

  const tenantsList = useMemo(() => {
    const rows = tenantsData?.data || [];
    return rows.map((row) => ({
      ...row,
      name: row.name || "N/A",
      code: row.code || "N/A",
      phone: row.phone || "N/A",
      email: row.email || "N/A",
      registrationDate: row.createdAt
        ? new Date(row.createdAt).toLocaleString([], {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "N/A",
      statusLabel: row.tenant_status == 1 ? "Active" : "Inactive",
      setupLabel: row.step !== null && row.step < 7 ? "Not Done" : "Done",
    }));
  }, [tenantsData]);

  const getTenantPath = useCallback((row) => {
    return row.step !== null && row.step < 7
      ? `/tenant/add/${row.code}`
      : `/tenant/edit/${row.code}`;
  }, []);

  const tenantColumns = useMemo(
    () => [
      { Header: "Full Name", accessor: "name" },
      { Header: "Tenant Code", accessor: "code" },
      { Header: "Phone", accessor: "phone" },
      { Header: "Email", accessor: "email" },
      { Header: "Registration Date", accessor: "registrationDate" },
      {
        Header: "Status",
        accessor: "statusLabel",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerClassName: "text-center",
        className: "text-center",
        Cell: ({ value }) => (
          <span className={`badge ${value === "Active" ? "badge-success" : "badge-danger"}`}>
            {value}
          </span>
        ),
      },
      {
        Header: "Setup",
        accessor: "setupLabel",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        headerClassName: "text-center",
        className: "text-center",
        Cell: ({ value }) => (
          <span className={`badge ${value === "Done" ? "badge-info" : "badge-warning"}`}>
            {value}
          </span>
        ),
      },
      {
        Header: "Actions",
        id: "actions",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        accessor: "code",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="text-right d-flex justify-content-end gap-2">
            <Button
              size="sm"
              color="link"
              className="btn-icon btn-link like table-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                navigate(getTenantPath(row.original));
              }}
            >
              <i className="tim-icons icon-pencil" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate, getTenantPath]
  );

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteUserMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const headerActions = (
    <Button
      size="sm"
      style={{ ...activeColorStyle, color: "white" }}
      onClick={() => navigate("/tenant/add")}
    >
      {t("routes.addTenant")}
    </Button>
  );

  const filters = (
    <TenantListFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      filterStatus={filterStatus}
      onFilterStatusChange={setFilterStatus}
      searchPlaceholder="Search by name, code, email, title, or phone..."
    />
  );

  const tableProps = {
    activeColor,
    data: tenantsList,
    columns: tenantColumns,
    manualPagination: true,
    pageCount: tenantsData?.pagination?.pageCount || 1,
    pageIndex: (tenantsData?.pagination?.page || page) - 1,
    totalItems: tenantsData?.pagination?.total,
    pageSize,
    noDataText: t("searchDeals.noRecordsFound"),
    loading: isLoading,
    loadingText: t("searchDeals.loading"),
    onPageChange: (pageIndex) => setPage(pageIndex + 1),
    onPageSizeChange: (newSize) => {
      setPageSize(newSize);
      setPage(1);
    },
    onRowClick: (row) => {
      navigate(getTenantPath(row));
    },
  };

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
    >
      {isTheme2 ? (
        <TenantsTable {...tableProps} className="-striped -highlight sticky-stock-table" />
      ) : (
        <TenantsTable
          {...tableProps}
          showPaginationTop
          showPaginationBottom={false}
          className="-striped -highlight"
        />
      )}
    </div>
  );

  return (
    <>
      <div
        className={classNames("content", "search-tenants", {
          "search-tenants--theme2": isTheme2,
          "theme2-admin-content-compact": isTheme2,
          "theme2-tenants-inset": isTheme2,
        })}
      >
        <TenantListCards
          isTheme2={isTheme2}
          filters={filters}
          table={table}
          headerActions={headerActions}
          title={t("routes.tenant")}
        />
      </div>

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">Delete User</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this user?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteUserMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default TenantList;
